import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Popover,
  Button,
  Checkbox,
  Stack,
  Box,
  Card,
  CircularProgress,
} from '@mui/material';
import { CaretRight } from '@phosphor-icons/react';
import { StatusCodes } from 'http-status-codes';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import UserExtraDetails from './userExtraDetails/userExtraDetails';

import trashCanIcon from '../../../../assets/svg/TrashCanIcon.svg';
import { axiosInstanceForTenantAPI } from '../../../../assets/utils/axiosInstance';
import { setupRoles } from '../../../../assets/utils/helpers';
import { ROLE_VALUES } from '../../../../constants/customerConstant';
import { useAuth } from '../../../../contexts/auth/authContext';
import { useLocalizedDepartmentData } from '../../../../hooks/fetcher/useDepartmentData';
import { useToastNotificationContext } from '../../../../hooks/global/alert/useToastNotification';
import DepartmentObject from '../../../../interfaces/departmentObject';
import { UserData as Data } from '../../../../interfaces/UserData';
import AccessRoleModal from '../../../accessRolesModalComponents/accessRolesModalComponent';
import HeaderTextComponent from '../../../common/headerTextComponent';

import { SwitchCompanySelect } from '~/features/shared/CompanySelector';
type InviteStatus = 'Pending' | 'Expired' | 'Accepted';

interface ErrorType {
  errorType: boolean;
  message: string;
}

interface Role {
  type: string;
  value: number;
}

interface InviteUserProps {
  setData: React.Dispatch<React.SetStateAction<Data[]>>;
  data: Data[];
  fetchLoading: boolean;
  errorState: ErrorType;
  departments: DepartmentObject[];
  roles: Role[];
  allRoles: Role[];
}

// style
const tableHeaderStyle = {
  color: '#878787',
  fontFamily: 'Schibsted Grotesk',
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '15px',
  letterSpacing: '0em',
};

const UserList = ({
  errorState,
  fetchLoading,
  data,
  setData,
  departments,
  roles,
  allRoles,
}: InviteUserProps) => {
  const { slug } = useAuth();

  const { createToastNotification, openConfirmationModal } =
    useToastNotificationContext();
  const intl = useIntl();

  const [selectedData, setSelectedData] = useState<Data | null>(null);
  const [anchorRole, setAnchorRole] = useState<null | HTMLElement>(null);
  const [anchorDept, setAnchorDept] = useState<null | HTMLElement>(null);
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const [openModal, setOpenModal] = useState(false);
  const [userFullName, setUserFullName] = useState<string | null>('');
  const [usersId, setUsersId] = useState(0);
  const [userRole, setUserRole] = useState(0);
  const userType = localStorage.getItem('user_type');
  const [slicedRole, setSlicedRole] = useState<Role[]>([]);
  const [departmentName, setDepartmentName] = useState<string>('');
  const { getLocalizedDepartmentName } = useLocalizedDepartmentData();

  const [allChecked, setAllChecked] = useState(true);

  const getInviteStatusStyle = (status: InviteStatus) => {
    const defaultStyle = {
      width: '30%',
      borderRadius: '14px',
      textAlign: 'center',
      paddingTop: '2px',
      color: '#1ED07A',
      backgroundColor: '#D2F6E4',
      paddingBottom: '2px',
    };

    if (status === 'Pending' || status === 'Expired') {
      const statusStyles = {
        Pending: {
          color: '#FF9900',
          backgroundColor: '#FFEFD7',
        },
        Expired: {
          color: '#E51D1D',
          backgroundColor: '#FFF1F1',
        },
      };

      const mergedStyle = {
        ...defaultStyle,
        ...(status ? statusStyles[status] : {}),
      };
      return mergedStyle;
    } else return defaultStyle;
  };

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  useEffect(() => {
    const rolesCopy = [...roles];
    if (roles.length != 0) setSlicedRole(rolesCopy.slice(0, 2));
  }, [roles]);

  const handleCellClick = (
    event: React.MouseEvent<HTMLElement>,
    rowData: Data,
    fieldType: 'user_type' | 'department_name',
  ) => {
    setSelectedData(rowData);
    if (fieldType === 'user_type') {
      setAnchorRole(event.currentTarget);
      setAnchorDept(null);
    }
    if (fieldType === 'department_name') {
      setAnchorDept(event.currentTarget);
      setAnchorRole(null);
    }
  };

  const handleClosePopover = () => {
    setAnchorRole(null);
    setAnchorDept(null);
  };

  // Update Role
  const handleRoleChange = async (user_type: number) => {
    const userToUpdate = data.find((user) => user.id === selectedData?.id);

    const response = await axiosInstanceForTenantAPI(companySlug).patch(
      '/update-user',
      {
        users: [{ ...userToUpdate, user_type: user_type }],
      },
    );

    if (response.status === StatusCodes.OK) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );

      setData((prevData) =>
        (prevData || []).map((item: Data) =>
          item.id === (selectedData ? selectedData.id : -1)
            ? { ...item, user_type }
            : item,
        ),
      );
      setAnchorRole(null);
    }
  };

  // Update Department
  const handleDepartmentChange = async (
    department_name: string,
    department: number,
  ) => {
    const userToUpdate = data.find((user) => user.id === selectedData?.id);
    const response = await axiosInstanceForTenantAPI(companySlug).patch(
      '/update-user',
      {
        users: [{ ...userToUpdate, department_name, department }],
      },
    );

    if (response.status === StatusCodes.OK) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );

      setData((prevData) =>
        (prevData || []).map((item: Data) =>
          item.id === selectedData?.id
            ? { ...item, department_name, department }
            : item,
        ),
      );
      setAnchorDept(null);
    }
  };

  const handleActiveChange = (isChecked: boolean, id: number) => {
    setData((prevData) =>
      (prevData || []).map((item: Data) =>
        item.id === id ? { ...item, is_active: !isChecked } : item,
      ),
    );
  };

  const openUpdateAccessModal = () => {
    setOpenModal(true);
  };

  const closeUpdateAccessModal = () => {
    setOpenModal(false);
  };

  // Remove Bulk User Action
  const handleBulkUpdateAction = async () => {
    const filteredData = data.filter(
      (item) =>
        item.is_active === false &&
        (item.user_settings_invite_status === 'Expired' ||
          item.user_settings_invite_status === 'Accepted'),
    );

    filteredData.forEach((user: Data) => {
      user.is_removed = true;
    });

    const response = await axiosInstanceForTenantAPI(companySlug).patch(
      '/update-user',
      {
        users: filteredData,
      },
    );

    if (response.status === StatusCodes.OK) {
      createToastNotification(
        intl.formatMessage({
          defaultMessage: 'User details successfully updated.',
        }),
        'success',
      );

      const targetIdSet = new Set(filteredData.map((obj) => obj.id));
      const updatedData = data.filter((item) => !targetIdSet.has(item.id));
      setData(updatedData);
    }
  };

  // Remove Bulk User Confirmation
  const handleBulkUpdate = () => {
    openConfirmationModal(
      'Remove User Confirmation',
      'Please confirm if you want to remove this user.',
      'Cancel',
      'Confirm',
      () => {
        handleBulkUpdateAction();
      },
    );
  };

  // Check or Uncheck all users
  const handleCheckAll = () => {
    setAllChecked(!allChecked);
    setData((prevData) =>
      (prevData || []).map((item: Data) => ({
        ...item,
        is_active: !allChecked,
      })),
    );
  };

  // Remove Single User Action
  const handleRemoveSingleUserAction = async (id: number) => {
    const userToRemove = data.find((user) => user.id === id);

    if (userToRemove) {
      const response = await axiosInstanceForTenantAPI(companySlug).patch(
        '/update-user',
        {
          users: [{ ...userToRemove, is_removed: true }],
        },
      );

      if (response.status === StatusCodes.OK) {
        createToastNotification(
          intl.formatMessage({
            defaultMessage: 'User details successfully updated.',
          }),
          'success',
        );
        const updatedData = data.filter((user) => user.id !== id);
        setData(updatedData);
      }
    }
  };

  // Remove Single user
  const handleRemoveSingleUser = async (id: number) => {
    openConfirmationModal(
      'Remove User Confirmation',
      'Please confirm if you want to remove this user.',
      'Cancel',
      'Confirm',
      () => {
        handleRemoveSingleUserAction(id);
      },
    );
  };

  const handleUpdateAccessClick = (
    fullName: string | null,
    userId: number,
    role: number,
    department_name: string | null,
  ) => {
    setUserFullName(fullName);
    setUsersId(userId);
    setUserRole(role);
    setDepartmentName(department_name !== null ? department_name : '');
    openUpdateAccessModal();
  };

  let content;

  if (data.length === 0 || !data) {
    content = (
      <TableBody>
        <TableRow>
          <TableCell
            align='center'
            colSpan={7}
            style={{
              width: '100%',
              fontSize: '20px',
            }}
          >
            {fetchLoading ? (
              <CircularProgress
                size={20}
                style={{ marginRight: '8px', color: 'black' }}
              />
            ) : (
              <FormattedMessage defaultMessage='No team members are currently invited.' />
            )}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (errorState?.errorType) {
    content = (
      <TableBody>
        <TableRow>
          <TableCell
            align='center'
            colSpan={7}
            style={{
              width: '100%',
              fontSize: '20px',
            }}
          >
            {errorState?.message}
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  if (data.length > 0) {
    content = (
      <>
        <TableBody>
          {data.map((row) => (
            <TableRow key={row.id}>
              <TableCell
                sx={{
                  alignItems: 'center',
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '17px',
                  letterSpacing: '0em',
                  textAlign: 'center',
                }}
                align='center'
              >
                {row.user_settings_invite_status !== 'Pending' && (
                  <Checkbox
                    checked={!row.is_active}
                    onChange={(event) =>
                      handleActiveChange(event.target.checked, row.id)
                    }
                  />
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '17px',
                  letterSpacing: '0em',
                }}
                align='left'
              >
                {row.full_name}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '17px',
                  letterSpacing: '0em',
                  '&:hover': {
                    textDecoration: 'underline', // Add underline on hover
                  },
                }}
                align='left'
                onClick={(event) => handleCellClick(event, row, 'user_type')}
                style={{
                  cursor: 'pointer',
                }}
              >
                {allRoles.find((option) => option.value === +row.user_type)
                  ?.type ? (
                  setupRoles(
                    allRoles.find((option) => option.value === +row.user_type)
                      ?.type,
                  )
                ) : (
                  <CircularProgress
                    size={20}
                    style={{ marginRight: '8px', color: 'black' }}
                  />
                )}
              </TableCell>
              <TableCell
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '17px',
                  letterSpacing: '0em',
                  '&:hover': {
                    textDecoration: 'underline', // Add underline on hover
                  },
                }}
                align='left'
                onClick={(event) =>
                  handleCellClick(event, row, 'department_name')
                }
                style={{ cursor: 'pointer' }}
              >
                {row.department_name
                  ? getLocalizedDepartmentName(row.department_name)
                  : '----'}
              </TableCell>
              {userType === `${ROLE_VALUES.TENANT_ADMIN}` && (
                <TableCell
                  sx={{
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: '17px',
                    letterSpacing: '0em',
                  }}
                  align='left'
                >
                  <Box
                    sx={{
                      ...getInviteStatusStyle(row.user_settings_invite_status),
                      padding: '2px 8px',
                      gap: '10px',
                      minWidth: '100px',
                      maxWidth: '200px',
                      textAlign: 'center',
                      paddingTop: '2px',
                      paddingBottom: '2px',
                    }}
                  >
                    {row.user_settings_invite_status}
                  </Box>
                </TableCell>
              )}
              {userType === `${ROLE_VALUES.TENANT_ADMIN}` ||
              userType === `${ROLE_VALUES.PARTNER_ADMIN}` ? (
                <TableCell
                  onClick={() => {
                    //pass the row.fullname as a parameter for the modal
                    handleUpdateAccessClick(
                      row.full_name,
                      row.id,
                      row.user_type,
                      row.department_name,
                    );
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div className='flex flex-row items-center'>
                    {' '}
                    <FormattedMessage defaultMessage='Update Access' />
                    <CaretRight size={16} className='ml-4' />
                  </div>
                </TableCell>
              ) : (
                <></>
              )}

              <TableCell
                sx={{
                  alignItems: 'center',
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: '400',
                  lineHeight: '17px',
                  letterSpacing: '0em',
                  textAlign: 'center',
                }}
                align='center'
              >
                {row.user_settings_invite_status !== 'Pending' && (
                  <Button
                    sx={{
                      width: 'auto', // Set the width to fit the contents (similar to "Hug")
                      borderRadius: '50%', // Make the button round (circular)
                      padding: '8px', // Add padding as needed for visual appearance
                    }}
                    onClick={() => handleRemoveSingleUser(row.id)}
                  >
                    <img
                      src={trashCanIcon}
                      alt='trashcan-icon'
                      style={{
                        width: '20px',
                        height: '20px',
                      }}
                    />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>

        <Popover
          open={Boolean(anchorRole)}
          anchorEl={anchorRole}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 0,
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: -110,
          }}
        >
          {selectedData && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '246px',
                maxHeight: '400px',
                overflow: 'auto',
                borderRadius: '6px',
                backgroundColor: '#FFF',
              }}
            >
              {slicedRole.map((item, index) => (
                <Button
                  sx={{
                    width: '100%',
                    margin: '0',
                    color: '#000',
                    fontFamily: 'Schibsted Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    textTransform: 'none',
                    transition: 'background-color 0.3s',
                    p: '10px 28px 10px 28px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 206, 207, 0.3)',
                    },
                  }}
                  key={index}
                  onClick={() => handleRoleChange(item.value)}
                >
                  {setupRoles(item.type)}
                </Button>
              ))}
            </div>
          )}
        </Popover>

        <Popover
          open={Boolean(anchorDept)}
          anchorEl={anchorDept}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 0,
          }}
          transformOrigin={{
            vertical: 'center',
            horizontal: -110,
          }}
        >
          {selectedData && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxWidth: '246px',
                maxHeight: '400px',
                overflow: 'auto',
                borderRadius: '6px',
                backgroundColor: '#FFF',
              }}
            >
              {departments.map((item, index) => (
                <Button
                  sx={{
                    width: '100%',
                    margin: '0',
                    color: '#000',
                    fontFamily: 'Schibsted Grotesk',
                    fontStyle: 'normal',
                    fontWeight: 400,
                    lineHeight: 'normal',
                    textTransform: 'none',
                    transition: 'background-color 0.3s',
                    p: '10px 28px 10px 28px',
                    textAlign: 'left',
                    justifyContent: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 206, 207, 0.3)',
                    },
                  }}
                  key={index}
                  onClick={() => handleDepartmentChange(item.name, item.id)}
                >
                  {item.name}
                </Button>
              ))}
            </div>
          )}
        </Popover>
      </>
    );
  }

  return (
    <>
      <Box sx={{ width: '100%' }}>
        <Card
          sx={{
            borderRadius: '6px',
            border: '1px solid #00000029',
            boxShadow: '0px 0px 4px 0px #0000000A',
            width: '100%',
            opacity: '1',
            pointerEvents: 'auto',
          }}
        >
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={{ p: 2.5, pb: 2 }}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
              gap={1}
            >
              <HeaderTextComponent variant='h1'>
                <FormattedMessage defaultMessage='User List' />
              </HeaderTextComponent>
              {(userType === `${ROLE_VALUES.PARTNER_USER}` ||
                userType === `${ROLE_VALUES.PARTNER_ADMIN}`) && (
                <div className='w-fit'>
                  <SwitchCompanySelect />
                </div>
              )}
            </Stack>
            <Button
              variant='outlined'
              sx={{
                width: '110px',
                height: '40px',
                borderRadius: '48px',
                border: '2px solid gray',
                padding: '8px, 12px, 8px, 12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                textTransform: 'none',
                color: '#181818',
                '&:hover': {
                  border: '2px solid #333333',
                },
              }}
              onClick={handleBulkUpdate}
            >
              <img
                src={trashCanIcon}
                alt='trashcan-icon'
                style={{
                  width: '20px',
                  height: '20px',
                }}
              />
              <FormattedMessage defaultMessage='Remove' />
            </Button>
          </Stack>

          <Table
            aria-label='simple table'
            sx={{ overflowX: 'auto', width: '100%' }}
          >
            <TableHead>
              <TableRow>
                {/* box icon */}
                <TableCell
                  sx={{
                    ...tableHeaderStyle,
                    width: '5%',
                  }}
                  align='center'
                >
                  {/* "Check All" checkbox with custom icons */}
                  <Checkbox
                    checked={!allChecked}
                    onChange={handleCheckAll}
                    icon={<AddIcon />}
                    checkedIcon={<RemoveIcon />}
                  />
                </TableCell>

                {/* Name */}
                <TableCell
                  sx={{
                    ...tableHeaderStyle,
                    width: '22%',
                  }}
                  align='left'
                >
                  <FormattedMessage defaultMessage='Name' />
                </TableCell>

                {/* Role */}
                <TableCell
                  sx={{
                    ...tableHeaderStyle,
                    width: '17%',
                  }}
                  align='left'
                >
                  <FormattedMessage defaultMessage='Role' />
                </TableCell>
                <TableCell
                  sx={{
                    ...tableHeaderStyle,
                    width: '17%',
                  }}
                  align='left'
                >
                  <FormattedMessage defaultMessage='Department' />
                </TableCell>
                {userType === `${ROLE_VALUES.TENANT_ADMIN}` && (
                  <TableCell
                    sx={{
                      ...tableHeaderStyle,
                      width: '17%',
                    }}
                    align='left'
                  >
                    <FormattedMessage defaultMessage='Invite Status' />
                  </TableCell>
                )}
                {userType === `${ROLE_VALUES.TENANT_ADMIN}` ||
                userType === `${ROLE_VALUES.PARTNER_ADMIN}` ? (
                  <TableCell
                    sx={{
                      ...tableHeaderStyle,
                      width: '17%',
                    }}
                    align='left'
                  >
                    <FormattedMessage defaultMessage='User Role' />
                  </TableCell>
                ) : (
                  <></>
                )}
                <TableCell
                  sx={{
                    ...tableHeaderStyle,
                    width: '8%',
                  }}
                  align='center'
                >
                  <FormattedMessage defaultMessage='Action' />
                </TableCell>
              </TableRow>
            </TableHead>

            {content}
          </Table>
        </Card>
      </Box>

      <AccessRoleModal
        open={openModal}
        handleClose={closeUpdateAccessModal}
        userName={userFullName}
        userId={usersId}
        userRole={userRole}
        slots={{
          extraDetails: (userId: number) => (
            <UserExtraDetails
              slug={companySlug !== null ? companySlug : ''}
              allRoles={allRoles}
              roleId={userRole}
              departmentName={departmentName}
              userId={userId}
              getInviteStatusStyle={getInviteStatusStyle}
            />
          ),
        }}
      />
    </>
  );
};

export default UserList;
